import React, { useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Eye, EyeOff, KeyRound, ArrowRight, AlertTriangle } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const isExpired = useMemo(() => {
    if (!token) return true;
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.exp * 1000 < Date.now();
    } catch (e) {
      if (process.env.NODE_ENV === "development") console.warn("Password reset parse error:", e);
      return true;
    }
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("As senhas não coincidem");
      return;
    }
    if (password.length < 8) {
      toast.error("Senha deve ter pelo menos 8 caracteres");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/password-reset/confirm", { token, new_password: password });
      setDone(true);
      toast.success("Senha redefinida com sucesso!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (!token || isExpired) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-4" data-testid="reset-password-expired">
        <div className="w-full max-w-[400px] text-center">
          <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-zinc-900">Link expirado ou inválido</h1>
          <p className="mt-2 text-sm text-zinc-500">
            Este link de redefinição expirou ou já foi utilizado. Solicite um novo.
          </p>
          <Link to="/esqueci-senha">
            <Button className="mt-6 bg-[#0055FF] hover:bg-[#0044CC] text-white" data-testid="reset-new-link">
              Solicitar novo link
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-4" data-testid="reset-password-page">
      <div className="w-full max-w-[400px]">
        <div className="flex items-center gap-2.5 mb-8 justify-center">
          <div className="h-9 w-9 rounded-md bg-[#0055FF] text-white flex items-center justify-center font-bold">Z</div>
          <div className="text-[15px] font-semibold tracking-tight">Portal Zemo</div>
        </div>

        {done ? (
          <div className="text-center" data-testid="reset-password-done">
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-zinc-900">Senha redefinida!</h1>
            <p className="mt-2 text-sm text-zinc-500">Sua senha foi alterada com sucesso. Faça login com a nova senha.</p>
            <Link to="/login">
              <Button className="mt-6 bg-[#0055FF] hover:bg-[#0044CC] text-white" data-testid="reset-go-login">
                Fazer login <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 text-center">
              Nova senha
            </h1>
            <p className="mt-2 text-sm text-zinc-500 text-center">
              Escolha uma nova senha para sua conta.
            </p>

            <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="reset-password-form">
              <div className="space-y-1.5">
                <Label>Nova senha</Label>
                <div className="relative">
                  <Input
                    type={showPwd ? "text" : "password"} value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Mínimo 8 caracteres" required minLength={8}
                    className="h-11 pr-10" data-testid="reset-password-input"
                  />
                  <button type="button" onClick={() => setShowPwd((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600" tabIndex={-1}>
                    {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Confirmar nova senha</Label>
                <Input
                  type="password" value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Repita a senha" required minLength={8}
                  className="h-11" data-testid="reset-confirm-input"
                />
              </div>
              <Button type="submit" disabled={submitting}
                className="w-full h-11 bg-[#0055FF] hover:bg-[#0044CC] text-white font-semibold"
                data-testid="reset-submit">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Redefinir senha"}
              </Button>
            </form>
          </>
        )}

        <div className="mt-8 flex items-center gap-2 text-[11px] text-zinc-400 justify-center">
          <KeyRound className="h-3 w-3" />
          <span>Conexão segura · TLS 1.3</span>
        </div>
      </div>
    </div>
  );
}
